import React, { useState, useEffect } from 'react';
import axiosInstance from '../apis/axiosConfig';
import { 
    FaShieldAlt, FaCheck, FaTimes, FaSearch, 
    FaIdCard, FaUserCheck, FaUserTimes, FaClock
} from 'react-icons/fa';
import '../pages_premium.css';
import './DriverVerificationsList.css';
import AdminPagination from '../components/common/AdminPagination';

export default function DriverVerificationsList() {
    const [verifications, setVerifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const fetchVerifications = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/driver-verification/admin/all');
            setVerifications(res.data || []);
        } catch (e) {
            console.error('Fetch verifications error:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVerifications();
    }, []);

    const updateStatus = async (id, status) => {
        try {
            await axiosInstance.put(`/driver-verification/admin/update/${id}`, { status });
            fetchVerifications();
        } catch (e) {
            console.error('Update verification status error:', e);
        }
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    // Filter Logic
    const filteredVerifications = verifications.filter((v) => {
        const matchesSearch = 
            (v.User?.name || '').toLowerCase().includes(search.toLowerCase()) ||
            (v.User?.email || '').toLowerCase().includes(search.toLowerCase()) ||
            (v.licence_type || '').toLowerCase().includes(search.toLowerCase()) ||
            (v.state_issued || v.country_issued || '').toLowerCase().includes(search.toLowerCase());

        const matchesStatus = 
            statusFilter === 'all' || 
            (statusFilter === 'pending' ? (v.status === 'pending' || !v.status) : v.status === statusFilter);

        const matchesCategory = 
            categoryFilter === 'all' || 
            (v.licence_category || '').toLowerCase() === categoryFilter.toLowerCase();

        return matchesSearch && matchesStatus && matchesCategory;
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [search, statusFilter, categoryFilter]);

    const paginatedVerifications = filteredVerifications.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    // Counts
    const totalCount = verifications.length;
    const verifiedCount = verifications.filter(v => v.status === 'verified').length;
    const pendingCount = verifications.filter(v => v.status === 'pending' || !v.status).length;
    const unsuccessfulCount = verifications.filter(v => v.status === 'unsuccessful').length;

    return (
        <div className="driver-verifications-page page-premium-wrapper">
            <div className="page-premium-content">
                
                {/* Hero Header */}
                <div className="page-hero-header">
                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaShieldAlt />
                        </div>
                        <div>
                            <h1 className="ph-title">Driver Verification Requests</h1>
                            <p className="ph-subtitle">Review &amp; manage verified driver licence details for CarEase</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{totalCount}</span>
                            <span className="count-lbl">Total Requests</span>
                        </div>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="dv-stats-grid">
                    <button type="button" className={`dv-stat-card ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')}>
                        <div className="dv-stat-icon blue">
                            <FaIdCard />
                        </div>
                        <div className="dv-stat-info">
                            <span className="dv-stat-num">{totalCount}</span>
                            <span className="dv-stat-label">All Submissions</span>
                        </div>
                    </button>

                    <button type="button" className={`dv-stat-card ${statusFilter === 'verified' ? 'active' : ''}`} onClick={() => setStatusFilter('verified')}>
                        <div className="dv-stat-icon green">
                            <FaUserCheck />
                        </div>
                        <div className="dv-stat-info">
                            <span className="dv-stat-num">{verifiedCount}</span>
                            <span className="dv-stat-label">Verified &amp; Approved</span>
                        </div>
                    </button>

                    <button type="button" className={`dv-stat-card ${statusFilter === 'pending' ? 'active' : ''}`} onClick={() => setStatusFilter('pending')}>
                        <div className="dv-stat-icon amber">
                            <FaClock />
                        </div>
                        <div className="dv-stat-info">
                            <span className="dv-stat-num">{pendingCount}</span>
                            <span className="dv-stat-label">Pending Review</span>
                        </div>
                    </button>

                    <button type="button" className={`dv-stat-card ${statusFilter === 'unsuccessful' ? 'active' : ''}`} onClick={() => setStatusFilter('unsuccessful')}>
                        <div className="dv-stat-icon red">
                            <FaUserTimes />
                        </div>
                        <div className="dv-stat-info">
                            <span className="dv-stat-num">{unsuccessfulCount}</span>
                            <span className="dv-stat-label">Unsuccessful / Rejected</span>
                        </div>
                    </button>
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    {/* Toolbar */}
                    <div className="dv-toolbar">
                        <div className="dv-toolbar-left">
                            <div className="dv-toolbar-title">
                                <span className="dv-title-dot"></span>
                                Driver Licences ({filteredVerifications.length})
                            </div>
                        </div>

                        <div className="dv-toolbar-right">
                            {/* Search Box */}
                            <div className="dv-search-box">
                                <FaSearch className="dv-search-icon" />
                                <input 
                                    type="text" 
                                    placeholder="Search user, state, country..." 
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>

                            {/* Category Filter */}
                            <select 
                                className="dv-filter-select" 
                                value={categoryFilter} 
                                onChange={(e) => setCategoryFilter(e.target.value)}
                            >
                                <option value="all">All Categories</option>
                                <option value="australian">Australian</option>
                                <option value="international">International</option>
                            </select>

                            {/* Status Filter */}
                            <select 
                                className="dv-filter-select" 
                                value={statusFilter} 
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="all">All Statuses</option>
                                <option value="pending">Pending</option>
                                <option value="verified">Verified</option>
                                <option value="unsuccessful">Unsuccessful</option>
                            </select>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="dv-table-wrap">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-danger" role="status"></div>
                                <p className="mt-2 text-muted small font-weight-bold">Loading driver verifications...</p>
                            </div>
                        ) : (
                            <table className="dv-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>User Details</th>
                                        <th>Category</th>
                                        <th>Licence Type</th>
                                        <th>State / Country</th>
                                        <th>Experience</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedVerifications.map((v, idx) => (
                                        <tr key={v.id}>
                                            <td className="text-muted font-weight-bold">{(currentPage - 1) * pageSize + idx + 1}</td>
                                            <td>
                                                <div className="dv-user-cell">
                                                    <div className="dv-user-avatar">
                                                        {getInitials(v.User?.name)}
                                                    </div>
                                                    <div>
                                                        <div className="dv-user-name">{v.User?.name || `User #${v.user_id}`}</div>
                                                        <div className="dv-user-email">{v.User?.email || 'N/A'}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`dv-badge-cat ${v.licence_category === 'australian' ? 'australian' : 'international'}`}>
                                                    {v.licence_category === 'australian' ? '🇦🇺 Australian' : '🌐 International'}
                                                </span>
                                            </td>
                                            <td className="font-weight-bold text-dark">{v.licence_type || 'Full Licence'}</td>
                                            <td>
                                                <span className="font-weight-bold text-slate-700">{v.state_issued || v.country_issued || 'N/A'}</span>
                                            </td>
                                            <td>
                                                <span className="dv-exp-badge">{v.years_held_calculated || 'N/A'}</span>
                                            </td>
                                            <td>
                                                <span className={`dv-badge-status ${v.status === 'verified' ? 'verified' : v.status === 'unsuccessful' ? 'unsuccessful' : 'pending'}`}>
                                                    {v.status === 'verified' && <FaCheck size={10} />}
                                                    {v.status === 'unsuccessful' && <FaTimes size={10} />}
                                                    {v.status === 'pending' && <FaClock size={10} />}
                                                    {v.status || 'pending'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="dv-actions">
                                                    {v.status !== 'verified' && (
                                                        <button 
                                                            type="button" 
                                                            onClick={() => updateStatus(v.id, 'verified')}
                                                            className="dv-action-btn verify"
                                                            title="Approve Driver Verification"
                                                        >
                                                            <FaCheck size={11} /> Verify
                                                        </button>
                                                    )}
                                                    {v.status !== 'unsuccessful' && (
                                                        <button 
                                                            type="button" 
                                                            onClick={() => updateStatus(v.id, 'unsuccessful')}
                                                            className="dv-action-btn reject"
                                                            title="Reject Verification"
                                                        >
                                                            <FaTimes size={11} /> Reject
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}

                                    {filteredVerifications.length === 0 && (
                                        <tr>
                                            <td colSpan="8">
                                                <div className="dv-empty">
                                                    <FaShieldAlt className="dv-empty-icon" />
                                                    <p className="font-weight-bold text-dark mb-1">No driver verifications found</p>
                                                    <p className="text-muted small">Try changing search query or filters.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <AdminPagination
                        totalItems={filteredVerifications.length}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(newSize) => { setPageSize(newSize); setCurrentPage(1); }}
                        itemLabel="verifications"
                    />
                </div>

            </div>
        </div>
    );
}
